import React, { useMemo } from 'react';
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import LegalDocumentContent from './LegalDocumentContent';
import {
  getCookieSections,
  getPrivacySections,
  getTermsSections,
  LEGAL_TITLES,
  LegalDocType,
} from './legalContent';
import { setCookieConsent } from '../../utils/cookieConsent';

interface LegalModalProps {
  doc: LegalDocType | null;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (doc: LegalDocType) => void;
}

const LegalModal: React.FC<LegalModalProps> = ({ doc, isOpen, onClose, onNavigate }) => {
  const toast = useToast();

  const navActions = useMemo(
    () => ({
      openTerms: () => onNavigate('terms'),
      openPrivacy: () => onNavigate('privacy'),
    }),
    [onNavigate]
  );

  if (!doc) return null;

  const sections =
    doc === 'terms'
      ? getTermsSections(navActions)
      : doc === 'privacy'
        ? getPrivacySections(navActions)
        : getCookieSections();

  const cookieFooter =
    doc === 'cookies' ? (
      <HStack pt={2} flexWrap="wrap" gap={2}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setCookieConsent(false);
            toast({ title: 'Essential cookies only', status: 'info', duration: 2500 });
          }}
        >
          Essential only
        </Button>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => {
            setCookieConsent(true);
            toast({ title: 'Analytics cookies enabled', status: 'success', duration: 2500 });
          }}
        >
          Allow analytics
        </Button>
      </HStack>
    ) : undefined;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader borderBottomWidth="1px">{LEGAL_TITLES[doc]}</ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6}>
          <LegalDocumentContent
            title={LEGAL_TITLES[doc]}
            sections={sections}
            showDisclaimer={doc !== 'cookies'}
            showTitle={false}
            footer={cookieFooter}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default LegalModal;
